/** AI B2B Leads: a dropdown with a search box for choosing several values from a long list (industries, departments). */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface Props {
  id: string;
  label: string;
  options: readonly string[];
  value: string[];
  onChange: (next: string[]) => void;
  /** How many may be chosen. */
  max?: number;
}

export function SearchableMultiSelect({ id, label, options, value, onChange, max }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const atMax = max !== undefined && value.length >= max;

  const toggle = (option: string) => {
    if (value.includes(option)) onChange(value.filter((x) => x !== option));
    else if (!atMax) onChange([...value, option]);
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={label}
            className="w-full justify-between font-normal"
            data-testid={id}
          >
            <span className={cn("truncate", value.length === 0 && "text-muted-foreground")}>
              {value.length === 0 ? t("prospectLeads.filters.chooseMany") : t("prospectLeads.filters.selectedCount", { count: value.length })}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[16rem] p-0" align="start">
          <Command>
            <CommandInput placeholder={t("prospectLeads.filters.searchPlaceholder")} data-testid={`${id}-search`} />
            <CommandList className="max-h-64">
              <CommandEmpty>{t("prospectLeads.filters.noMatch")}</CommandEmpty>
              <CommandGroup>
                {options.map((o) => {
                  const on = value.includes(o);
                  return (
                    <CommandItem key={o} value={o} disabled={!on && atMax} onSelect={() => toggle(o)} data-testid={`${id}-option-${o}`}>
                      <Check className={cn("mr-2 h-4 w-4", on ? "opacity-100" : "opacity-0")} aria-hidden />
                      <span className="truncate">{o}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
          {atMax && <p className="border-t px-3 py-2 text-xs text-muted-foreground" data-testid={`${id}-max`}>{t("prospectLeads.filters.maxReached", { max })}</p>}
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={`${id}-chosen`}>
          {value.map((v) => (
            <Badge key={v} variant="secondary" className="gap-1 pr-1" data-testid={`${id}-chip-${v}`}>
              {v}
              <button
                type="button"
                className="rounded-sm p-0.5 hover:bg-muted-foreground/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t("prospectLeads.filters.removeValue", { value: v })}
                onClick={() => toggle(v)}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </Badge>
          ))}
          {value.length > 1 && (
            <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => onChange([])} data-testid={`${id}-clear`}>
              {t("prospectLeads.filters.clearAll")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
